import React, { useEffect, useState, useRef } from 'react';
import './NumberInput.scss';
import { InputNumber } from 'antd';

interface NumberInputProps {
  label: string;
  value?: number;
  setValue?: (value: number) => void;
  defaultValue?: number;
  error?: string | React.ReactElement;
  isDisabled?: boolean;
}

export const NumberInput = (props: NumberInputProps) => {
  const { label, value, setValue = () => { }, defaultValue = 0, error, isDisabled } = props;
  let [innerValue, setInnerValue] = useState<number | undefined>(value !== undefined ? value : undefined);
  const previousValueRef = useRef<number | undefined>(value);

  const [currentError, setCurrentError] = useState(error);

  const inputFocused = useRef(false);
  const [blurred, setBlurred] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isEmptyInputRef = useRef(false);
  const userTypedValueRef = useRef<number | null>(null); // Track if user explicitly typed a value
  const justRestoredDefaultRef = useRef(false); // Track if we just restored defaultValue in onBlur

  const showError = Boolean(false);

  // Check if the value has been modified from the default
  // Only consider it modified if the input has been focused (user interaction)
  // Use the actual value prop first, then fall back to innerValue
  const actualValue = value !== undefined ? value : (innerValue !== undefined ? innerValue : defaultValue);
  const currentDisplayValue = actualValue === null ? 0 : actualValue;
  const isModified = inputFocused.current && currentDisplayValue !== defaultValue;

  const className = `${!inputFocused.current ? 'loan-input placeholder' : 'loan-input'} ${showError ? 'error' : ''} ${isModified ? 'modified' : ''}`;

  useEffect(() => {
    setCurrentError(error);
  }, [blurred])

  useEffect(() => {
    if (currentError !== error && blurred) {
      setCurrentError(error);
    }
  }, [error, blurred])

  useEffect(() => {
    if (inputFocused.current && innerValue !== undefined) {
      setValue(innerValue)
    }
  }, [innerValue])

  useEffect(() => {
    if (value !== undefined && value !== innerValue) {
      setInnerValue(value)
      previousValueRef.current = value;
    }
  }, [value])

  // Determine the value to display in the input
  // If input has been focused and user cleared it, show undefined (empty)
  // Otherwise, show the value or defaultValue
  const displayValue = (() => {
    // If input is focused and innerValue is undefined, show undefined (empty input)
    if (inputFocused.current && innerValue === undefined) {
      return undefined;
    }
    // If innerValue is defined, show it
    if (innerValue !== undefined) {
      return innerValue;
    }
    // Only show defaultValue if input hasn't been focused yet
    return inputFocused.current ? undefined : defaultValue;
  })();

  return (
    <div className={className}>
      <p>{label}</p>
      <InputNumber
        className={`number-input-field ${isFocused ? 'focused' : ''}`}
        addonBefore={"$"}
        variant="borderless"
        formatter={(value) => value !== null && value !== undefined ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
        parser={(value: string | undefined) => {
          if (!value || value.trim() === '') {
            isEmptyInputRef.current = true; // Mark that input is empty
            return 0; // Return 0 as placeholder for parser type
          }
          isEmptyInputRef.current = false; // Input has content
          const parsed = Number(value.replace(/\$\s?|(,*)/g, ''));
          const result = isNaN(parsed) ? 0 : parsed;
          // If user typed something (even if it's 0), mark it
          if (isFocused && result !== null && result !== undefined) {
            userTypedValueRef.current = result;
          }
          return result;
        }}
        value={displayValue}
        onChange={(value: number | null) => {
          // If we just restored defaultValue in onBlur, ignore this onChange
          // This prevents the restored value from being treated as "empty input"
          if (justRestoredDefaultRef.current) {
            previousValueRef.current = defaultValue;
            justRestoredDefaultRef.current = false;
            return;
          }

          // If value is null, it means the input is empty (user cleared it)
          if (value === null) {
            setInnerValue(undefined);
            previousValueRef.current = undefined;
            userTypedValueRef.current = null; // User cleared, no value typed
            return;
          }

          // If value is 0, we need to distinguish between "input is empty" and "user typed 0"
          // Strategy: If isEmptyInputRef is true AND previousValueRef was not 0, then it's empty
          // If isEmptyInputRef is false OR previousValueRef was 0, then user is typing 0
          if (value === 0) {
            if (isEmptyInputRef.current && previousValueRef.current !== 0 && previousValueRef.current !== undefined) {
              // Input is empty (user cleared it)
              setInnerValue(undefined);
              previousValueRef.current = undefined;
              userTypedValueRef.current = null; // Input is empty, no value typed
              return;
            } else {
              // User is typing 0 explicitly
              userTypedValueRef.current = 0;
              setInnerValue(0);
              previousValueRef.current = 0;
              return;
            }
          }

          // Otherwise, it's an explicit value (not 0, not null)
          // Mark that user typed this value explicitly
          userTypedValueRef.current = value;
          setInnerValue(value);
          previousValueRef.current = value;
        }}
        onFocus={() => {
          if (!inputFocused.current) {
            inputFocused.current = true;
          }
          setIsFocused(true);
          // Store the current value before user starts editing
          previousValueRef.current = innerValue !== undefined ? innerValue : defaultValue;
          // Reset userTypedValueRef to track if user types something new
          userTypedValueRef.current = null;
          // Reset justRestoredDefaultRef flag
          justRestoredDefaultRef.current = false;
        }}
        onBlur={() => {
          setBlurred(true);
          setIsFocused(false);

          // If user didn't type any value (userTypedValueRef is null), restore defaultValue
          // This means user cleared the input and didn't type anything new
          if (userTypedValueRef.current === null) {
            if (innerValue === undefined) {
              setInnerValue(defaultValue);
              setValue(defaultValue);
              previousValueRef.current = defaultValue;
              userTypedValueRef.current = null; // Reset for next time
              justRestoredDefaultRef.current = true; // Mark that we just restored defaultValue
              isEmptyInputRef.current = false; // Input now has a value (defaultValue), so it's not empty
            }
          } else {
            // User typed a value (including 0), keep it
            const valueToKeep = userTypedValueRef.current;
            setInnerValue(valueToKeep);
            setValue(valueToKeep);
            previousValueRef.current = valueToKeep;
            justRestoredDefaultRef.current = false;
          }
          userTypedValueRef.current = null;
        }}
        disabled={isDisabled}
      />
      {showError && <p className='error-message'>{currentError}</p>}
    </div>
  )
}

/* Utils */
const locale = "en-us";

export const currencyFormatter = (value: number, digits = 2) => {
  const r = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits
  }).format(value);

  return r.substring(1)
};

export const currencyParser = (val: string) => {
  val = '$'.concat(val)

  try {
    // for when the input gets clears
    if (typeof val === "string" && !val.length) {
      val = "0.0";
    }

    // detecting and parsing between comma and dot
    var group = new Intl.NumberFormat(locale).format(1111).replace(/1/g, "");
    var decimal = new Intl.NumberFormat(locale).format(1.1).replace(/1/g, "");
    var reversedVal = val.replace(new RegExp("\\" + group, "g"), "");
    reversedVal = reversedVal.replace(new RegExp("\\" + decimal, "g"), ".");
    //  => 1232.21 €

    // removing everything except the digits and dot
    reversedVal = reversedVal.replace(/[^0-9.]/g, "");
    //  => 1232.21

    // appending digits properly
    const digitsAfterDecimalCount = (reversedVal.split(".")[1] || []).length;
    const needsDigitsAppended = digitsAfterDecimalCount > 2;

    if (needsDigitsAppended) {
      reversedVal = (Number(reversedVal) * Math.pow(10, digitsAfterDecimalCount - 2)).toString();
    }

    return Number.isNaN(reversedVal) ? 0 : reversedVal;
  } catch (error) {
    console.error(error);
  }
};